<script lang="ts">
  import type { TaskViewPreferences } from '../lib/types';
  import { SORT_BY_LABELS, SORT_DIRECTION_LABELS, GROUP_BY_LABELS } from '../lib/taskSort';
  import { jsonHeaders } from '../lib/apiHelpers';
  import {
    copyTasksWithToast,
    describeTaskCopyFields,
    TASK_COPY_FORMAT_LABELS,
    type TaskCopyFormat,
  } from '../lib/taskClipboard';
  import { taskCopyPreferences } from '../lib/stores/taskCopyPreferences';
  import type { Task } from '../lib/types';
  import TaskCopyFieldsPicker from './TaskCopyFieldsPicker.svelte';

  interface Props {
    preferences: TaskViewPreferences;
    onUpdate: (prefs: TaskViewPreferences) => void;
    onBulkMoveClick?: (anchorEl: HTMLElement) => void;
    /** Incomplete tasks in display order; enables the "Copy incomplete" actions. */
    getCopyTasks?: () => Task[];
    /** Markdown `#` heading for copied tasks (usually the project title). */
    copyHeading?: string;
    /** Disable copy actions (e.g. while tasks are still loading). */
    copyDisabled?: boolean;
  }

  let {
    preferences,
    onUpdate,
    onBulkMoveClick,
    getCopyTasks,
    copyHeading,
    copyDisabled = false,
  }: Props = $props();

  function handleCopy(format: TaskCopyFormat) {
    if (!getCopyTasks || copyDisabled) return;
    close();
    void copyTasksWithToast(getCopyTasks(), format, copyHeading);
  }

  let isOpen = $state(false);
  let showCopyFields = $state(false);
  let popoverEl: HTMLDivElement | undefined = $state();
  let triggerEl: HTMLButtonElement | undefined = $state();

  // Optimistic overrides: applied immediately on user action, auto-invalidated when
  // the preferences prop changes (e.g., after the parent processes onUpdate or after
  // Inertia re-delivers server prefs). We track which preferences "version" the overrides
  // were set against so stale overrides are ignored without needing a $effect.
  let localOverrides = $state<Partial<TaskViewPreferences>>({});
  let overridesPrefsKey = $state('');
  let prefsKey = $derived(
    `${preferences.sort_by}|${preferences.sort_direction}|${preferences.group_by}`,
  );

  let sortBy = $derived.by(() => {
    if (overridesPrefsKey === prefsKey) return localOverrides.sort_by ?? preferences.sort_by;
    return preferences.sort_by;
  });
  let sortDirection = $derived.by(() => {
    if (overridesPrefsKey === prefsKey)
      return localOverrides.sort_direction ?? preferences.sort_direction;
    return preferences.sort_direction;
  });
  let groupBy = $derived.by(() => {
    if (overridesPrefsKey === prefsKey) return localOverrides.group_by ?? preferences.group_by;
    return preferences.group_by;
  });

  function setOverrides(overrides: Partial<TaskViewPreferences>) {
    localOverrides = overrides;
    overridesPrefsKey = prefsKey;
  }

  // Let the click bubble so other open menus/popovers see it as an outside
  // click and close; handleWindowClick ignores clicks inside popoverEl.
  function toggle() {
    isOpen = !isOpen;
    showCopyFields = false;
  }

  function close() {
    isOpen = false;
  }

  async function applyChange(field: string, value: string) {
    const updated: TaskViewPreferences = {
      sort_by: sortBy,
      sort_direction: sortDirection,
      group_by: groupBy,
      [field]: value,
    };

    // Smart direction defaults when switching sort field
    if (field === 'sort_by') {
      if (value === 'position' || value === 'title') {
        updated.sort_direction = 'asc';
      } else if (value === 'priority' || value === 'created_at') {
        // High-first / newest-first is the common expectation
        updated.sort_direction = 'desc';
      }
    }

    // Update local overrides immediately for responsiveness.
    // These auto-invalidate when the parent updates the preferences prop via onUpdate.
    setOverrides({
      sort_by: updated.sort_by,
      sort_direction: updated.sort_direction,
      group_by: updated.group_by,
    });

    // Notify parent
    onUpdate(updated);

    // Persist to DB
    try {
      await fetch('/preferences/task_view', {
        method: 'PATCH',
        headers: jsonHeaders(),
        body: JSON.stringify(updated),
      });
    } catch (e) {
      console.error('Failed to save task view preferences:', e);
    }
  }

  // Close popover when clicking outside
  function handleWindowClick(e: MouseEvent) {
    if (isOpen && popoverEl && !popoverEl.contains(e.target as Node)) {
      close();
    }
  }

  // Check if current prefs differ from defaults
  let isCustomized = $derived(
    preferences.sort_by !== 'position' ||
      preferences.sort_direction !== 'asc' ||
      preferences.group_by !== 'none',
  );
</script>

<svelte:window onclick={handleWindowClick} />

<div class="view-options-container" bind:this={popoverEl}>
  <button
    type="button"
    class="view-options-trigger"
    class:active={isOpen || isCustomized}
    onclick={toggle}
    title="Task view options"
    bind:this={triggerEl}
  >
    <svg class="gear-icon" viewBox="0 0 20 20" fill="currentColor">
      <path
        fill-rule="evenodd"
        d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z"
        clip-rule="evenodd"
      />
    </svg>
  </button>

  {#if isOpen}
    <!-- svelte-ignore a11y_no_static_element_interactions, a11y_click_events_have_key_events -->
    <div class="popover" onclick={(e) => e.stopPropagation()}>
      <div class="popover-header">
        <span class="popover-title">View Options</span>
      </div>

      <!-- Sort By -->
      <div class="option-group">
        <span class="option-label">Sort by</span>
        <div class="option-buttons">
          {#each Object.entries(SORT_BY_LABELS) as [value, label]}
            <button
              type="button"
              class="option-btn"
              class:selected={sortBy === value}
              onclick={() => applyChange('sort_by', value)}
            >
              {label}
            </button>
          {/each}
        </div>
      </div>

      <!-- Sort Direction (hidden for manual/position sort) -->
      {#if sortBy !== 'position'}
        <div class="option-group">
          <span class="option-label">Direction</span>
          <div class="option-buttons">
            {#each Object.entries(SORT_DIRECTION_LABELS) as [value, label]}
              <button
                type="button"
                class="option-btn"
                class:selected={sortDirection === value}
                onclick={() => applyChange('sort_direction', value)}
              >
                {value === 'asc' ? '↑' : '↓'}
                {label}
              </button>
            {/each}
          </div>
        </div>
      {/if}

      <!-- Group By -->
      <div class="option-group">
        <span class="option-label">Group by</span>
        <div class="option-buttons">
          {#each Object.entries(GROUP_BY_LABELS) as [value, label]}
            <button
              type="button"
              class="option-btn"
              class:selected={groupBy === value}
              onclick={() => applyChange('group_by', value)}
            >
              {label}
            </button>
          {/each}
        </div>
      </div>

      {#if onBulkMoveClick || getCopyTasks}
        <div class="option-group option-group-actions">
          <span class="option-label">Bulk actions</span>
          {#if onBulkMoveClick}
            <button
              type="button"
              class="action-btn"
              onclick={() => {
                if (!triggerEl) return;
                onBulkMoveClick(triggerEl);
                close();
              }}
            >
              📦 Move all incomplete…
            </button>
          {/if}
          {#if getCopyTasks}
            <span class="action-sublabel">
              📋 {copyDisabled ? 'Loading tasks…' : 'Copy all incomplete as'}
            </span>
            <div class="option-buttons">
              {#each Object.entries(TASK_COPY_FORMAT_LABELS) as [format, label]}
                <button
                  type="button"
                  class="option-btn"
                  disabled={copyDisabled}
                  onclick={() => handleCopy(format as TaskCopyFormat)}
                >
                  {label}
                </button>
              {/each}
            </div>
            <button
              type="button"
              class="include-toggle"
              aria-expanded={showCopyFields}
              aria-controls="task-view-copy-fields"
              onclick={() => (showCopyFields = !showCopyFields)}
            >
              <span class="include-chevron" class:open={showCopyFields} aria-hidden="true">›</span>
              <span class="include-toggle-label">Include:</span>
              <span class="include-toggle-summary"
                >{describeTaskCopyFields($taskCopyPreferences)}</span
              >
            </button>
            {#if showCopyFields}
              <div id="task-view-copy-fields" class="include-fields">
                <TaskCopyFieldsPicker />
              </div>
            {/if}
          {/if}
        </div>
      {/if}

      <!-- Reset -->
      {#if isCustomized}
        <div class="popover-footer">
          <button
            type="button"
            class="reset-btn"
            onclick={() => {
              const resetPrefs: TaskViewPreferences = {
                sort_by: 'position',
                sort_direction: 'asc',
                group_by: 'none',
              };
              setOverrides(resetPrefs);
              onUpdate(resetPrefs);
              fetch('/preferences/task_view', {
                method: 'PATCH',
                headers: jsonHeaders(),
                body: JSON.stringify(resetPrefs),
              }).catch((e) => console.error('Failed to reset preferences:', e));
            }}
          >
            Reset to defaults
          </button>
        </div>
      {/if}
    </div>
  {/if}
</div>

<style>
  .view-options-container {
    position: relative;
  }

  .view-options-trigger {
    width: 1.25rem;
    height: 1.25rem;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #9ca3af;
    background: none;
    border: none;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.15s;
    padding: 0;
  }

  /* 44px hit area around the 20px gear on phones */
  @media (max-width: 767px) {
    .view-options-trigger {
      position: relative;
    }

    .view-options-trigger::after {
      content: '';
      position: absolute;
      inset: -12px;
    }
  }

  .view-options-trigger:hover,
  .view-options-trigger.active {
    color: #6b7280;
    background: rgba(0, 0, 0, 0.06);
  }

  .gear-icon {
    width: 0.8125rem;
    height: 0.8125rem;
  }

  .popover {
    position: absolute;
    top: calc(100% + 0.25rem);
    right: 0;
    width: 13rem;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
    z-index: 70;
    overflow: hidden;
  }

  .popover-header {
    padding: 0.375rem 0.625rem;
    border-bottom: 1px solid #f3f4f6;
  }

  .popover-title {
    font-size: 0.625rem;
    font-weight: 700;
    color: #374151;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .option-group {
    padding: 0.375rem 0.625rem;
  }

  .option-group + .option-group {
    border-top: 1px solid #f3f4f6;
  }

  .option-group-actions {
    background: #fcfcfd;
  }

  .option-label {
    display: block;
    font-size: 0.5625rem;
    font-weight: 600;
    color: #9ca3af;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin-bottom: 0.25rem;
  }

  .option-buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 0.1875rem;
  }

  .option-btn {
    padding: 0.1875rem 0.375rem;
    font-size: 0.625rem;
    font-weight: 500;
    color: #6b7280;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.1s;
    white-space: nowrap;
  }

  .option-btn:hover {
    background: #f3f4f6;
    border-color: #d1d5db;
    color: #374151;
  }

  .option-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .option-btn:disabled:hover {
    background: #f9fafb;
    border-color: #e5e7eb;
    color: #6b7280;
  }

  .option-btn.selected {
    background: #eff6ff;
    border-color: #3b82f6;
    color: #2563eb;
  }

  .action-btn {
    width: 100%;
    text-align: left;
    padding: 0.375rem 0.5rem;
    font-size: 0.6875rem;
    font-weight: 600;
    color: #1d4ed8;
    background: #eff6ff;
    border: 1px solid #dbeafe;
    border-radius: 0.3125rem;
    cursor: pointer;
  }

  .action-btn:hover {
    background: #dbeafe;
  }

  .action-sublabel {
    display: block;
    font-size: 0.625rem;
    font-weight: 500;
    color: #6b7280;
    margin-bottom: 0.1875rem;
  }

  .action-btn + .action-sublabel {
    margin-top: 0.375rem;
  }

  .include-toggle {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    width: 100%;
    margin-top: 0.3125rem;
    padding: 0.125rem 0;
    font-size: 0.5625rem;
    text-align: left;
    color: #9ca3af;
    background: none;
    border: none;
    cursor: pointer;
  }

  .include-toggle:hover {
    color: #4b5563;
  }

  .include-chevron {
    display: inline-block;
    width: 0.5rem;
    font-size: 0.75rem;
    line-height: 1;
    transition: transform 0.15s;
  }

  .include-chevron.open {
    transform: rotate(90deg);
  }

  .include-toggle-label {
    font-weight: 600;
  }

  .include-toggle-summary {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .include-fields {
    margin: 0.125rem -0.375rem 0;
  }

  .popover-footer {
    padding: 0.375rem 0.625rem;
    border-top: 1px solid #f3f4f6;
  }

  .reset-btn {
    width: 100%;
    padding: 0.25rem;
    font-size: 0.5625rem;
    font-weight: 500;
    color: #6b7280;
    background: none;
    border: 1px solid #e5e7eb;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.1s;
  }

  .reset-btn:hover {
    color: #374151;
    background: #f9fafb;
    border-color: #d1d5db;
  }
</style>
